import React from "react";
import { Image, Platform, Pressable, ScrollView } from "~/ui/primitives";
import styled, { useTheme } from "~/ui/styled";
import { dismissKeyboardOnPress } from "~utils/keyboard";
import FastImage from "~/ui/FastImage";
import { StartFlex, ColumnFlex, CenterFlex, Text } from "~components/El";

const logo = require("~assets/images/app-intro-logo.png");
const logoUri = Image.resolveAssetSource(logo).uri;

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  logoSize?: number;
  bottomHeight?: string;
  onSubmit?: () => void;
}

const ContainerView = styled.View`
  position: relative;
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.primary};
`;

const TopContent = styled(StartFlex)`
  width: 100%;
  height: auto;
  min-height: 160px;
  padding: 0 12px 20px 12px;
  align-items: flex-end;
`;

const Logo = styled(CenterFlex)<{ size: number }>`
  width: ${({ size }) => size}px;
  height: auto;
  border-radius: ${({ size }) => size / 2}px;
  background-color: ${({ theme }) => theme.colors.background};
  overflow: hidden;
`;

const BottomCard = styled(ColumnFlex)`
  flex: 5;
  width: 100%;
  min-height: 0;
  border-top-left-radius: ${({ theme }) => theme.radii["3xl"]};
  border-top-right-radius: ${({ theme }) => theme.radii["3xl"]};
  background-color: ${({ theme }) => theme.colors.blockBackground};
  z-index: 1;
`;

const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  logoSize = 72,
  onSubmit,
}) => {
  const theme = useTheme();
  return (
    <ContainerView
      {...(dismissKeyboardOnPress ? { onClick: dismissKeyboardOnPress } : {})}
    >
      <TopContent>
        <StartFlex height={'auto'} width="100%">
          <Logo size={logoSize}>
            <FastImage
              style={{ height: "100%", width: "100%" }}
              src={logoUri}
              resizeMode={FastImage.resizeMode.stretch}
            />
          </Logo>
          <ColumnFlex ml="20px">
            <Text
              color={theme.colors.textLight}
              fontSize={theme.fontSizes["3xl"]}
              fontWeight="600"
            >
              {title}
            </Text>
            {subtitle ? (
              <Text
                color={theme.colors.textLight}
                fontSize={theme.fontSizes.md}
                fontWeight="600"
                mt="8px"
              >
                {subtitle}
              </Text>
            ) : null}
          </ColumnFlex>
        </StartFlex>
      </TopContent>

      <BottomCard>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={{ flex: 1 ,width: '100%'}}
          contentContainerStyle={{ flexGrow: 1 }}
        >
          <form
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              width: "100%",
              minHeight: 0,
            }}
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit?.();
            }}
          >
            {children}
          </form>
        </ScrollView>
      </BottomCard>
    </ContainerView>
  );
};

export default AuthLayout;
